"use client";

import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import {
  FileText,
  Clock,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  UserCheck,
  Calendar,
  DollarSign,
  TrendingUp,
  Folder
} from "lucide-react";
import { format } from "date-fns";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/utils/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { showSuccess, showError } from "@/utils/toast";
import { ClientNPS } from "./ClientNPS";

interface ClientDashboardProps {
  projects: any[];
  invoices: any[];
  time_logs: any[];
}

export const ClientDashboard: React.FC<ClientDashboardProps> = ({ projects, invoices, time_logs }) => {
  const { profile } = useAuth();
  const [showNPS, setShowNPS] = useState(false);

  // Fetch onboarding status
  const { data: onboardingData, isLoading: onboardingLoading } = useQuery({
    queryKey: ['client-onboarding', profile?.id],
    queryFn: async () => {
      if (!profile?.id) return null;
      const { data } = await supabase
        .from('profiles')
        .select('onboarding_completed, kyc_verified, mfa_enabled')
        .eq('id', profile.id)
        .single();
      return data;
    },
    enabled: !!profile?.id,
  });

  // Calculate onboarding progress
  const onboardingProgress = React.useMemo(() => {
    if (!onboardingData) return 0;
    const steps = [
      onboardingData.onboarding_completed,
      onboardingData.kyc_verified,
      onboardingData.mfa_enabled,
      profile?.email,
      profile?.first_name && profile?.last_name,
    ].filter(Boolean).length;
    return (steps / 5) * 100;
  }, [onboardingData, profile]);

  // Get recent activity
  const recentActivity = React.useMemo((): Array<{ type: 'invoice' | 'project'; title: string; date: string; status: string; amount?: number }> => {
    const activities: Array<{ type: 'invoice' | 'project'; title: string; date: string; status: string; amount?: number }> = [
      ...invoices.slice(0, 3).map(inv => ({
        type: 'invoice' as const,
        title: `Invoice #${inv.invoice_number}`,
        date: inv.created_at,
        status: inv.status,
        amount: inv.total_amount as number,
      })),
      ...projects.slice(0, 3).map(proj => ({
        type: 'project' as const,
        title: proj.name,
        date: proj.created_at,
        status: proj.status,
      }))
    ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 5);
    return activities;
  }, [invoices, projects]);

  const handleCompleteOnboarding = () => {
    setShowNPS(true);
  };

  const handleNPSComplete = async () => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ onboarding_completed: true })
        .eq('id', profile?.id);
      
      if (error) throw error;
      
      showSuccess('Welcome to WightWay Connect! Your onboarding is complete.');
      setShowNPS(false);
    } catch (error) {
      showError('Failed to complete onboarding');
    }
  };

  if (onboardingLoading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="p-6">
            <Skeleton className="h-4 w-48 mb-2" />
            <Skeleton className="h-2 w-full" />
          </CardContent>
        </Card>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => (
            <Card key={i}>
              <CardContent className="p-6">
                <Skeleton className="h-4 w-32 mb-2" />
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (showNPS) {
    return (
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <UserCheck className="h-5 w-5" />
              <span>Complete Your Onboarding</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-6">
              Help us improve WightWay Connect by sharing your experience so far.
            </p>
            <ClientNPS onComplete={handleNPSComplete} />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Onboarding Progress Card */}
      {!onboardingData?.onboarding_completed && (
        <Card className="border-blue-200 bg-blue-50">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <UserCheck className="h-5 w-5 text-blue-600" />
              <span>Complete Your Setup</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span>Profile Completion</span>
                  <span>{Math.round(onboardingProgress)}%</span>
                </div>
                <Progress value={onboardingProgress} className="h-2" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center space-x-2">
                  {onboardingData?.kyc_verified ? (
                    <CheckCircle className="h-4 w-4 text-green-600" />
                  ) : (
                    <AlertCircle className="h-4 w-4 text-orange-600" />
                  )}
                  <span className="text-sm">Identity Verification</span>
                </div>
                <div className="flex items-center space-x-2">
                  {onboardingData?.mfa_enabled ? (
                    <CheckCircle className="h-4 w-4 text-green-600" />
                  ) : (
                    <AlertCircle className="h-4 w-4 text-orange-600" />
                  )}
                  <span className="text-sm">Two-Factor Authentication</span>
                </div>
              </div>
              <Button onClick={handleCompleteOnboarding} className="w-full">
                Complete Onboarding
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Projects</CardTitle>
            <Folder className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{projects.length}</div>
            <p className="text-xs text-muted-foreground">
              {projects.filter(p => p.status === 'active').length} active
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Invoices</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{invoices.length}</div>
            <p className="text-xs text-muted-foreground">
              {invoices.filter(i => i.status === 'paid').length} paid
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Time Logged</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {Math.round(time_logs.reduce((sum, log) => sum + (log.duration_minutes || 0), 0) / 60)}h
            </div>
            <p className="text-xs text-muted-foreground">
              Across all projects
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <TrendingUp className="h-5 w-5" />
            <span>Recent Activity</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {recentActivity.length > 0 ? (
            <div className="space-y-3">
              {recentActivity.map((activity, index) => (
                <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                  <div className="flex items-center space-x-3">
                    {activity.type === 'invoice' ? (
                      <FileText className="h-4 w-4 text-blue-600" />
                    ) : (
                      <Folder className="h-4 w-4 text-green-600" />
                    )}
                    <div>
                      <p className="font-medium">{activity.title}</p>
                      <p className="text-sm text-muted-foreground">
                        {format(new Date(activity.date), 'MMM dd, yyyy')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {activity.type === 'invoice' && activity.amount && (
                      <Badge variant="outline">£{activity.amount.toFixed(2)}</Badge>
                    )}
                    <Badge variant={activity.status === 'paid' || activity.status === 'active' ? "default" : "secondary"}>
                      {activity.status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Alert>
              <AlertDescription>
                No recent activity. Your projects and invoices will appear here.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Calendar className="h-5 w-5" />
              <span>Upcoming Deadlines</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {projects.filter(p => p.due_date && new Date(p.due_date) > new Date()).slice(0, 3).length > 0 ? (
              <div className="space-y-2">
                {projects.filter(p => p.due_date && new Date(p.due_date) > new Date()).slice(0, 3).map(project => (
                  <div key={project.id} className="flex justify-between items-center p-2 rounded bg-muted/30">
                    <span className="text-sm">{project.name}</span>
                    <Badge variant="outline">{format(new Date(project.due_date), 'MMM dd')}</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No upcoming deadlines</p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <DollarSign className="h-5 w-5" />
              <span>Payment Summary</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm">Total Invoiced</span>
                <span className="font-medium">£{invoices.reduce((sum, inv) => sum + inv.total_amount, 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm">Outstanding</span>
                <span className="font-medium">£{invoices.filter(inv => inv.status === 'sent').reduce((sum, inv) => sum + inv.total_amount, 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm">Paid</span>
                <span className="font-medium text-green-600">£{invoices.filter(inv => inv.status === 'paid').reduce((sum, inv) => sum + inv.total_amount, 0).toFixed(2)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ClientDashboard;